import { Injectable, signal } from '@angular/core';

/** How long a toast stays (canon feedback vocabulary): 5s, paused while hovered or focused. */
export const TOAST_DURATION_MS = 5000;
/** At most three toasts at once; a fourth pushes the oldest out. */
export const TOAST_STACK_LIMIT = 3;

export interface ToastOptions {
  /** Translation key of the sentence. */
  messageKey: string;
  messageParams?: Record<string, unknown>;
  /** Offers Undo: called once, and the toast goes. */
  undo?: () => void;
}

export interface Toast extends ToastOptions {
  id: number;
}

/**
 * The confirmation toast (canon feedback vocabulary; first consumer NBO-2546): "saved", "removed",
 * "N updated" — a plate in the rail material at the bottom left of the content area, newest on top,
 * at most three, 5s each, paused while the pointer or focus is on the stack, with an optional Undo.
 *
 * CONFIRMATIONS ONLY. An error is never a toast — it is an inline message at the scope that failed,
 * or a notice the user must act on (that is how a success can no longer erase an error, which the
 * one MatSnackBar of NotificationService does). Screens move onto this per wave, never as a sweep.
 *
 * The stack renders in `<app-toast-stack>`, which the app shell holds.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly list = signal<Toast[]>([]);
  /** Newest first. */
  readonly toasts = this.list.asReadonly();

  private seq = 0;
  private paused = false;
  /** Per toast: its timer while running, and the time it has left. */
  private readonly timers = new Map<number, { handle?: ReturnType<typeof setTimeout>; remaining: number; startedAt: number }>();

  show(options: ToastOptions): number {
    const toast: Toast = { ...options, id: ++this.seq };
    const next = [toast, ...this.list()];
    for (const dropped of next.slice(TOAST_STACK_LIMIT)) this.clearTimer(dropped.id);
    this.list.set(next.slice(0, TOAST_STACK_LIMIT));
    this.timers.set(toast.id, { remaining: TOAST_DURATION_MS, startedAt: 0 });
    if (!this.paused) this.startTimer(toast.id);
    return toast.id;
  }

  dismiss(id: number): void {
    this.clearTimer(id);
    this.list.update((list) => list.filter((toast) => toast.id !== id));
  }

  /** Runs the toast's Undo and removes it. */
  undo(id: number): void {
    const toast = this.list().find((t) => t.id === id);
    this.dismiss(id);
    toast?.undo?.();
  }

  /** The pointer or focus entered the stack: every toast waits. */
  pause(): void {
    if (this.paused) return;
    this.paused = true;
    const now = Date.now();
    for (const timer of this.timers.values()) {
      if (timer.handle === undefined) continue;
      clearTimeout(timer.handle);
      timer.handle = undefined;
      timer.remaining = Math.max(0, timer.remaining - (now - timer.startedAt));
    }
  }

  resume(): void {
    if (!this.paused) return;
    this.paused = false;
    for (const id of this.timers.keys()) this.startTimer(id);
  }

  private startTimer(id: number): void {
    const timer = this.timers.get(id);
    if (!timer || timer.handle !== undefined) return;
    timer.startedAt = Date.now();
    timer.handle = setTimeout(() => this.dismiss(id), timer.remaining);
  }

  private clearTimer(id: number): void {
    const timer = this.timers.get(id);
    if (timer?.handle !== undefined) clearTimeout(timer.handle);
    this.timers.delete(id);
  }
}
